import React, { useEffect, useState, createContext, useContext, ReactNode } from 'react';
import { KeycloakService } from '../services/keycloakService';
import { setupGlobalAxiosInterceptors } from '../services/axiosInterceptor';
import { useTokenRefresh } from '../hooks/useTokenRefresh';
import { useUserActivity } from '../hooks/useUserActivity';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import { tokenExpirationService } from '../services/tokenExpirationService';
import { AuthErrorBoundary } from '../components/ErrorBoundaries/AuthErrorBoundary';

interface KeycloakContextType {
  initialized: boolean;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  initializeKeycloak: () => Promise<boolean>;
  updateAuthenticationState: (isAuth: boolean) => void;
  initializationError: string | null;
}

const KeycloakContext = createContext<KeycloakContextType>({
  initialized: false,
  isAuthenticated: false,
  isAuthenticating: true,
  initializeKeycloak: async () => false,
  updateAuthenticationState: () => {},
  initializationError: null
});

export const useKeycloak = () => useContext(KeycloakContext);

interface KeycloakProviderProps {
  children: ReactNode;
}

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [initializationError, setInitializationError] = useState<string | null>(null);

  // Sprawdzenie stanu uwierzytelnienia na podstawie lokalnie przechowywanych tokenów
  useEffect(() => {
    // Sprawdzamy czy są zapisane tokeny i ustawiamy odpowiedni stan
    setIsAuthenticating(true);
    const hasToken = localStorage.getItem('kc_token') || sessionStorage.getItem('kc_token');
    if (hasToken) {
      setIsAuthenticated(true);
    }
    // Stan weryfikacji zostanie zakończony przez initializeKeycloak
  }, []);

  const initializeKeycloak = async () => {
    try {
      setIsAuthenticating(true);
      const authenticated = await KeycloakService.init();
      // Wymuszamy konwersję na boolean
      setIsAuthenticated(!!authenticated);
      setInitialized(true);
      setInitializationError(null);
      console.log('Keycloak został zainicjalizowany lub zaktualizowano stan');
      setIsAuthenticating(false);
      return authenticated;
    } catch (error) {
      console.error('Błąd inicjalizacji Keycloak:', error);
      setInitializationError('Nie udało się zainicjować systemu uwierzytelniania.');
      setInitialized(true); // Ustawiamy initialized=true mimo błędu, aby aplikacja mogła funkcjonować
      setIsAuthenticating(false);
      return false;
    }
  };

  // Use the extracted hooks for token management
  const handleInactivityLogout = () => {
    console.log('Automatyczne wylogowanie z powodu bezczynności');
    
    // Wyczyść stan aplikacji
    updateAuthenticationState(false);
    
    // Wyczyść dane z localStorage/sessionStorage
    localStorage.removeItem('kc_token');
    sessionStorage.removeItem('kc_token');
    
    // NOWE: Bezpośrednie przekierowanie bez wywoływania keycloak.logout()
    // To uniknie pokazania okna wylogowania Keycloak
    // Dodaj parametr informujący o przyczynie wylogowania
    window.location.href = '/?reason=inactivity';
  };

  const handleTokenRefreshSuccess = () => {
    updateAuthenticationState(true);
  };

  const handleTokenRefreshError = () => {
    handleInactivityLogout();
  };

  // Initialize hooks for token management
  useTokenRefresh({
    onRefreshSuccess: handleTokenRefreshSuccess,
    onRefreshError: handleTokenRefreshError
  });

  useUserActivity({
    onActivity: handleTokenRefreshSuccess,
    onActivityError: handleTokenRefreshError
  });

  useInactivityTimeout({
    onInactivityLogout: handleInactivityLogout
  });

  useEffect(() => {
    // Initialize global Axios interceptors
    setupGlobalAxiosInterceptors();

    // Initialize Keycloak
    initializeKeycloak();

    // Start token expiration checking service
    if (KeycloakService.isLoggedIn()) {
      tokenExpirationService.updateOptions({
        onTokenExpired: handleInactivityLogout
      });
      tokenExpirationService.start();
    }

    return () => {
      // Cleanup token expiration service
      tokenExpirationService.stop();
    };
  }, []);

  // Funkcja do aktualizacji stanu uwierzytelnienia bez reinicjalizacji Keycloak
  const updateAuthenticationState = (isAuth: boolean) => {
    setIsAuthenticated(isAuth);
    setIsAuthenticating(false);
  };

  // Wyświetlanie komunikatów o błędach i ładowaniu
  if (initializationError) {
    return (
      <div className="keycloak-error">
        <h3>Błąd inicjalizacji</h3>
        <p>{initializationError}</p>
        <button onClick={() => window.location.reload()}>Spróbuj ponownie</button>
      </div>
    );
  }

  return (
    <AuthErrorBoundary
      onError={(error, errorInfo) => {
        console.error('Authentication error caught by boundary:', error, errorInfo);
        // Additional error reporting can be added here
      }}
    >
      <KeycloakContext.Provider
        value={{
          initialized,
          isAuthenticated,
          isAuthenticating,
          initializeKeycloak,
          updateAuthenticationState,
          initializationError
        }}
      >
        {children}
      </KeycloakContext.Provider>
    </AuthErrorBoundary>
  );
};
